import { useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const ROLE_OPTIONS = [
  { value: "author", label: "Author", hint: "Post real projects and receive applications" },
  { value: "beta_reader", label: "Beta Reader", hint: "Apply to real projects and give feedback" },
  { value: "both", label: "Both", hint: "Try Last Bookmark from both sides" },
];

/**
 * Shared Early Access request modal used from both the Dashboard access
 * card and the new dashboard/profile/jobs "limited account" banner.
 *
 * The parent controls open state and receives the created request row via
 * ``onSubmitted`` so it can flip its local status to "pending" without a
 * refetch.
 */
export default function EarlyAccessRequestDialog({ onClose, onSubmitted }) {
  const [choice, setChoice] = useState("both");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/private-tester-requests", {
        role_preference: choice,
        note: note.trim() || null,
      });
      toast.success("Request submitted — we'll email you when it's decided.");
      onSubmitted?.(data.request);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4 overflow-y-auto"
      data-testid="early-access-request-dialog-backdrop"
    >
      <form
        onSubmit={submit}
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-card shadow-2xl border max-h-[92vh] sm:max-h-[calc(100vh-4rem)] flex flex-col"
        data-testid="early-access-request-dialog"
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b bg-card rounded-t-2xl">
          <h3 className="font-serif text-2xl">Request Early Access</h3>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-muted" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 pt-4 pb-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            How would you like to test Last Bookmark? Your answer helps us
            match you with the right projects and reviewers.
          </p>
          <div className="mt-4 space-y-2">
            {ROLE_OPTIONS.map((opt) => {
              const active = choice === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                    active
                      ? "border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-forest))/0.05]"
                      : "border-border hover:bg-muted/40"
                  }`}
                  data-testid={`early-access-role-${opt.value}`}
                >
                  <input
                    type="radio"
                    name="role_preference"
                    value={opt.value}
                    checked={active}
                    onChange={() => setChoice(opt.value)}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium">{opt.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{opt.hint}</div>
                  </div>
                </label>
              );
            })}
          </div>
          <div className="mt-4">
            <label className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Anything you'd like to share? <span className="normal-case font-normal">(optional)</span>
            </label>
            <textarea
              className="sb-input mt-1 w-full min-h-[80px] resize-y leading-relaxed"
              placeholder="Genres you're excited about, why you'd like to test, etc."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={1000}
              data-testid="early-access-note-input"
            />
          </div>
        </div>
        <div
          className="flex flex-col-reverse sm:flex-row justify-end gap-2 px-6 py-4 border-t bg-card rounded-b-2xl"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          <button type="button" onClick={onClose} className="btn-ghost border w-full sm:w-auto" disabled={saving}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary w-full sm:w-auto"
            data-testid="early-access-submit"
          >
            {saving ? <><Loader2 className="animate-spin" size={14} /> Sending…</> : <><Send size={14} /> Submit request</>}
          </button>
        </div>
      </form>
    </div>
  );
}
